import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const ContactPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { t } = useTranslation();

  const [formData, setFormData] = useState({ name: '', phone: '', email: '', interests: [] as string[], message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleCheckbox = (val: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(val) ? prev.interests.filter(i => i !== val) : [...prev.interests, val],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    if (formData.message.length > 500) return;
    setSubmitting(true);
    setError(false);
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('interests', formData.interests.join(', '));
      body.append('message', formData.message);
      await fetch('https://readdy.ai/api/form/d7chrrmm3fsfopf23dng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const infoCards = [
    { icon: 'ri-map-pin-2-line', label: t('contact_location_label'), value: t('contact_location_val'), sub: t('contact_location_sub') },
    { icon: 'ri-time-line', label: t('contact_hours_label'), value: t('contact_hours_val'), sub: t('contact_hours_sub') },
    { icon: 'ri-ship-2-line', label: t('contact_provenance_label'), value: t('contact_provenance_val'), sub: t('contact_provenance_sub') },
  ];

  return (
    <>
      <Navbar />
      <main className="bg-[#0d0d0d] min-h-screen" style={{ fontFamily: "'Cormorant Garamond', serif" }}>

        {/* Hero */}
        <section className="relative h-[52vh] min-h-[380px] flex flex-col items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://magadenidestilados.com/wp-content/uploads/2025/11/Uisque-ao-Por-do-Sol.png')" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0d0d0d]" />
          <div className="relative z-10 text-center px-6 w-full">
            <p className="text-[#c9a84c]/70 text-[10px] tracking-[0.45em] uppercase mb-4 font-light" style={{ fontFamily: 'sans-serif' }}>{t('contact_hero_tagline')}</p>
            <h1 className="text-5xl md:text-6xl font-light text-white tracking-wide mb-4">{t('contact_hero_title')}</h1>
            <div className="flex items-center justify-center gap-2 text-white/35 text-xs" style={{ fontFamily: 'sans-serif' }}>
              <Link to="/" className="hover:text-[#c9a84c] transition-colors">{t('contact_breadcrumb_home')}</Link>
              <span>/</span>
              <span className="text-white/55">{t('contact_hero_title')}</span>
            </div>
          </div>
        </section>

        {/* Info cards */}
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5">
            {infoCards.map(card => (
              <div key={card.label} className="group bg-[#111] border border-white/8 hover:border-[#c9a84c]/30 rounded-xl p-7 flex flex-col gap-4 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-full border border-[#c9a84c]/25 group-hover:border-[#c9a84c]/50 transition-colors">
                  <i className={`${card.icon} text-[#c9a84c]/70 text-lg`}></i>
                </div>
                <div style={{ fontFamily: 'sans-serif' }}>
                  <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-1">{card.label}</p>
                  <p className="text-white/85 text-sm font-medium">{card.value}</p>
                  <p className="text-white/35 text-xs mt-0.5">{card.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Map + Form */}
        <section className="pb-28 px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-[#c9a84c]/60 text-[10px] tracking-[0.4em] uppercase mb-2 font-light" style={{ fontFamily: 'sans-serif' }}>{t('contact_map_label')}</p>
                <h2 className="text-3xl font-light text-white">{t('contact_map_title')}</h2>
              </div>
              <div className="w-full h-[340px] rounded-xl overflow-hidden border border-white/8">
                <iframe title="Map Natal RN" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d126419.37697498!2d-35.3295!3d-5.7945!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7b255b5d5b5b5b5b%3A0x0!2sNatal%2C%20RN%2C%20Brasil!5e0!3m2!1spt-BR!2sbr!4v1700000000000!5m2!1spt-BR!2sbr" width="100%" height="100%" style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) saturate(0.4) brightness(0.85)' }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
              </div>
              <div className="bg-[#111] border border-white/8 rounded-xl p-6" style={{ fontFamily: 'sans-serif' }}>
                <p className="text-white/30 text-[10px] tracking-[0.3em] uppercase mb-4">{t('contact_social_label')}</p>
                <div className="flex items-center gap-3">
                  {[
                    { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://instagram.com' },
                    { icon: 'ri-facebook-line', label: 'Facebook', href: 'https://facebook.com' },
                    { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: 'https://wa.me' },
                  ].map(s => (
                    <a key={s.label} href={s.href} target="_blank" rel="nofollow noopener noreferrer" className="flex items-center gap-2 border border-white/10 hover:border-[#c9a84c]/40 hover:text-[#c9a84c] text-white/45 text-xs px-4 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap">
                      <i className={`${s.icon} text-base`}></i>
                      {s.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div className="mb-7">
                <p className="text-[#c9a84c]/60 text-[10px] tracking-[0.4em] uppercase mb-2 font-light" style={{ fontFamily: 'sans-serif' }}>{t('contact_form_label')}</p>
                <h2 className="text-3xl font-light text-white">{t('contact_form_title')}</h2>
              </div>

              {submitted ? (
                <div className="border border-[#c9a84c]/30 rounded-xl p-14 text-center bg-[#c9a84c]/5 h-[480px] flex flex-col items-center justify-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full border border-[#c9a84c]/40 mb-6">
                    <i className="ri-check-line text-2xl text-[#c9a84c]"></i>
                  </div>
                  <p className="text-white text-2xl font-light mb-3">{t('contact_form_success')}</p>
                  <p className="text-white/40 text-sm font-light max-w-xs leading-relaxed" style={{ fontFamily: 'sans-serif' }}>{t('contact_form_success_sub')}</p>
                  <button onClick={() => { setSubmitted(false); setFormData({ name: '', phone: '', email: '', interests: [], message: '' }); }} className="mt-8 border border-[#c9a84c]/30 text-[#c9a84c]/70 hover:bg-[#c9a84c]/10 text-xs tracking-widest uppercase px-8 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap" style={{ fontFamily: 'sans-serif' }}>
                    {t('contact_form_new')}
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="bg-[#111] border border-white/8 rounded-xl p-8 space-y-5" style={{ fontFamily: 'sans-serif' }}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('contact_form_name')} <span className="text-[#c9a84c]">*</span></label>
                      <input name="name" type="text" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('contact_form_phone')}</label>
                      <input name="phone" type="tel" value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('contact_form_email')} <span className="text-[#c9a84c]">*</span></label>
                    <input name="email" type="email" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all" />
                  </div>
                  <div className="flex flex-col gap-2.5">
                    <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('contact_form_interest')}</label>
                    <div className="flex gap-4 flex-wrap">
                      {['Whiskey', 'Gin', 'Tasting', 'Special Collection'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                          <div onClick={() => handleCheckbox(opt)} className={`w-5 h-5 rounded border flex items-center justify-center transition-all cursor-pointer flex-shrink-0 ${formData.interests.includes(opt) ? 'bg-[#c9a84c] border-[#c9a84c]' : 'border-white/20 bg-transparent group-hover:border-[#c9a84c]/50'}`}>
                            {formData.interests.includes(opt) && <i className="ri-check-line text-black text-xs"></i>}
                          </div>
                          <span className="text-white/50 text-sm group-hover:text-white/75 transition-colors">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-white/40 text-[10px] tracking-[0.2em] uppercase">{t('contact_form_message')} <span className="text-[#c9a84c]">*</span></label>
                    <textarea name="message" required rows={5} maxLength={500} value={formData.message} onChange={e => setFormData(p => ({ ...p, message: e.target.value }))} className="bg-white/4 border border-white/10 rounded-lg px-4 py-3 text-white text-sm placeholder-white/15 focus:outline-none focus:border-[#c9a84c]/50 transition-all resize-none" />
                    <p className={`text-[10px] text-right ${formData.message.length > 500 ? 'text-red-400' : 'text-white/20'}`}>{formData.message.length}/500</p>
                  </div>
                  {error && <p className="text-red-400/80 text-xs" style={{ fontFamily: 'sans-serif' }}>{t('contact_form_error')}</p>}
                  <button type="submit" disabled={submitting || formData.message.length > 500} className="w-full relative overflow-hidden group disabled:opacity-40 cursor-pointer whitespace-nowrap rounded-lg py-4 transition-all duration-300" style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #a8863a 50%, #c9a84c 100%)', backgroundSize: '200% 100%' }}>
                    <span className="relative z-10 flex items-center justify-center gap-2 text-[#1a0f00] text-xs tracking-[0.3em] uppercase font-semibold">
                      {submitting ? <><i className="ri-loader-4-line animate-spin text-sm"></i>{t('contact_form_sending')}</> : <>{t('contact_form_send')}<i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i></>}
                    </span>
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 px-6 bg-[#080808] relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 60px)' }} />
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <p className="text-[#c9a84c]/60 text-[10px] tracking-[0.45em] uppercase mb-4 font-light" style={{ fontFamily: 'sans-serif' }}>{t('contact_cta_label')}</p>
            <h2 className="text-3xl md:text-4xl font-light text-white mb-5">{t('contact_cta_title')}</h2>
            <p className="text-white/40 text-sm font-light leading-relaxed mb-10 max-w-xl mx-auto" style={{ fontFamily: 'sans-serif' }}>{t('contact_cta_desc')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/loja" className="inline-flex items-center gap-2 rounded-lg px-10 py-4 text-[#1a0f00] text-xs tracking-[0.3em] uppercase font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 hover:opacity-90" style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #a8863a 50%, #c9a84c 100%)' }}>
                {t('contact_cta_btn')}<i className="ri-arrow-right-line text-sm"></i>
              </Link>
              <Link to="/our-story" className="inline-flex items-center gap-2 border border-white/15 hover:border-[#c9a84c]/40 text-white/55 hover:text-[#c9a84c] rounded-lg px-10 py-4 text-xs tracking-[0.3em] uppercase whitespace-nowrap cursor-pointer transition-all duration-300" style={{ fontFamily: 'sans-serif' }}>
                {t('contact_cta_story')}
              </Link>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
};

export default ContactPage;
